/** @jsxImportSource @emotion/react */
import {
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionSummary as MuiAccordionSummary,
  ClickAwayListener as MuiClickAwayListener,
  IconButton as MuiIconButton,
  ListItemButton as MuiListItemButton,
  ListItemText as MuiListItemText,
} from '@mui/material';
import * as Icons from '@zip/icons';
import { FC, useState, useEffect } from 'react';
import * as styles from './DrawerExpandableItem.styles';
import { DrawerExpandableItemProps } from './DrawerExpandableItemProps';

const DrawerExpandableItem: FC<DrawerExpandableItemProps> = ({
  id,
  item,
  innerSelectedItem,
  setInnerSelectedItem,
  autoSelectOnOpen = false,
}): React.ReactElement => {
  const checkIsChildSelected = (): boolean => {
    return Boolean(
      item?.children?.find((child) => child === innerSelectedItem)
    );
  };

  const [isMenuExpanded, setIsMenuExpanded] = useState<boolean>(false);

  const handleClickAway = (): void => {
    const childIsSelected = checkIsChildSelected();
    if (innerSelectedItem === item || childIsSelected) {
      return;
    }

    setIsMenuExpanded(false);
  };

  const handleExpandableClick = (): void => {
    setIsMenuExpanded(!isMenuExpanded);
    if (item?.children?.length > 0 && autoSelectOnOpen) {
      setInnerSelectedItem(item?.children?.[0], true);
    }
  };

  // Sync the expanded state with the selected item
  useEffect(() => {
    const childIsSelected = checkIsChildSelected();
    setIsMenuExpanded(childIsSelected);
  }, [innerSelectedItem, item?.children]);

  return (
    <MuiClickAwayListener onClickAway={handleClickAway}>
      <MuiAccordion css={styles.accordian} expanded={isMenuExpanded}>
        <MuiAccordionSummary
          expandIcon={
            <MuiIconButton disableRipple>
              <Icons.ChevronDown width="16" height="16" />
            </MuiIconButton>
          }
          aria-controls={`${id}-content`}
          id={`${id}-header`}
          css={styles.accordianSummary(
            innerSelectedItem?.value === item.value,
            isMenuExpanded
          )}
          onClick={handleExpandableClick}
        >
          {item.text}
        </MuiAccordionSummary>
        <MuiAccordionDetails css={styles.accordianDetails} id={`${id}-content`}>
          {item?.children?.map((child) => (
            <MuiListItemButton
              id={`${id}-${child.value?.toLowerCase()}`}
              key={child.text.toLowerCase()}
              css={styles.listItem(innerSelectedItem?.value === child.value)}
              onClick={(): void => {
                setInnerSelectedItem(child, false);
                if (child.onClick) {
                  child.onClick();
                }
              }}
              disableRipple
            >
              <MuiListItemText
                primary={child.text}
                css={styles.listItemText(true)}
              />
            </MuiListItemButton>
          ))}
        </MuiAccordionDetails>
      </MuiAccordion>
    </MuiClickAwayListener>
  );
};

export default DrawerExpandableItem;
